import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { companyState } from '../../lib/companySession';
import NoCompany from '../../pages/HR/NoCompany';

/*
 * Sits between ProtectedRoute and Layout on every /hr/* route. An HR session with no usable
 * tenant gets the explanation instead of an empty job list whose Create button 403s.
 *
 * Two of companyState()'s four states block; the other two fall through:
 *
 *   none        no membership at all — the unprovisioned front door.
 *   suspended   attached, but the EMPLOYER was suspended. It has to be caught here and not just
 *               folded into `none`: every /hr/* route 403s no_company for this recruiter exactly
 *               as it does for an unprovisioned one, so without this branch a suspended tenant's
 *               recruiter would be shown "your account isn't attached to a company yet" about a
 *               company they can name — and would go chasing an admin to attach an account that
 *               is already attached.
 *   incomplete  active but missing name/website/description. NOT blocked: everything except
 *               publishing and generating tasks works. CompanyIncompleteNotice says so in place,
 *               inside the Layout, rather than a gate throwing the whole app away over it.
 *   ready       through.
 *
 * Reads `activeCompany` — the PICKED tenant — not `company`, which is membership row 0 whatever
 * ?companyId= the rest of the app is sending. It cannot change which screen a single-company
 * recruiter sees (with one membership the two are the same row, and with none `activeCompany`
 * falls back to `company` so the suspended report still arrives); it is what keeps this gate and
 * CompanyIncompleteNotice describing the same employer as every other request.
 *
 * Like ProtectedRoute this is a UX branch, not a security boundary — requireCompany throws
 * 403 no_company on the server whatever the client renders.
 */
const CompanyGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, activeCompany } = useAuth();
  if (user?.userType !== 'hr') return <>{children}</>;

  const state = companyState(activeCompany);
  if (state === 'none') return <NoCompany variant="none" />;
  if (state === 'suspended') return <NoCompany variant="suspended" />;
  return <>{children}</>;
};

export default CompanyGate;
